import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  IconButton,
  Stack,
  Typography,
  TextField,
  MenuItem,
} from "@mui/material";
import {
  LocalizationProvider,
  StaticDatePicker,
  DatePicker,
} from "@mui/x-date-pickers";
import type { DateView } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/ja";
import utc from "dayjs/plugin/utc";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { default as CustomButton } from "../Buttons/Button/CustomButton";
import { useMediaQuery } from "@mui/material";
import { ThemeProvider, createTheme, useTheme } from "@mui/material/styles";
import { useResponsiveMenuPlacement } from "../hooks/useResponsiveMenuPlacement";

import {
  CalendarLeftArrowIcon,
  CalendarRightArrowIcon,
  ClockIcon,
  DateRangeCalendarIcon,
  DropDownArrowIcon,
} from "./CalendarIcons";
import "./CustomCalendar.css";

// Set Japanese locale
dayjs.locale("ja");
dayjs.extend(utc);

export interface CustomDatePickerProps {
  onDateConfirm: (date: Dayjs | null) => void;
  onCancel?: () => void;
  type?: "date" | "dateTime";
  initialDate?: Dayjs | null;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  disabled?: boolean;
  locale?: string;
}

export interface CustomDateRangePickerProps {
  onDateConfirm: (dates: { start: Dayjs | null; end: Dayjs | null }) => void;
  onCancel?: () => void;
  initialStartDate?: Dayjs | null;
  initialEndDate?: Dayjs | null;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  disabled?: boolean;
  locale?: string;
}

export const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  onDateConfirm,
  onCancel,
  type = "date",
  initialDate = dayjs(),
  minDate,
  maxDate,
  disabled = false,
  locale = "ja",
}) => {
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(initialDate);
  const [monthToShow, setMonthToShow] = useState<Dayjs>(initialDate || dayjs());
  const [showTimePicker, setShowTimePicker] = useState(false);

  dayjs.locale(locale);

  const handlePrev = () => {
    const newMonth = monthToShow.subtract(1, "month");
    setMonthToShow(newMonth);
  };

  const handleNext = () => {
    const newMonth = monthToShow.add(1, "month");
    setMonthToShow(newMonth);
  };

  const japaneseWeekdays = ["日", "月", "火", "水", "木", "金", "土"];

  const handleTimeChange = (
    unit: "hour" | "minute" | "second",
    direction: "up" | "down",
  ) => {
    if (!selectedDate) return;

    const currentValue = selectedDate.get(unit);
    let newValue = direction === "up" ? currentValue + 1 : currentValue - 1;

    if (unit === "hour") newValue = (newValue + 24) % 24;
    if (unit === "minute" || unit === "second") newValue = (newValue + 60) % 60;

    const updatedDate = selectedDate.set(unit, newValue);
    setSelectedDate(updatedDate);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
      <Box
        width={322}
        border={1}
        borderRadius={2}
        p={2}
        className="customDatePicker"
        sx={{ opacity: disabled ? 0.6 : 1 }}
      >
        {/* Header */}
        <Box display="flex" justifyContent="center" alignItems="center" mb={1}>
          <IconButton onClick={handlePrev} disabled={disabled}>
            <span className="iconContainer">
              <CalendarLeftArrowIcon />
            </span>
          </IconButton>
          <Typography variant="subtitle1" className="dateTitle">
            {monthToShow.format("YYYY年 MM月")}
          </Typography>
          <IconButton onClick={handleNext} disabled={disabled}>
            <span className="iconContainer">
              <CalendarRightArrowIcon />
            </span>
          </IconButton>
        </Box>

        {/* Date Picker */}
        <StaticDatePicker
          displayStaticWrapperAs="desktop"
          value={selectedDate}
          onChange={(newDate) => !disabled && setSelectedDate(newDate)}
          dayOfWeekFormatter={(_day: string, date: Dayjs) =>
            japaneseWeekdays[date.day()]
          }
          showDaysOutsideCurrentMonth={false}
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
          sx={{
            "& .MuiPickersCalendarHeader-root": {
              display: "none",
            },
            "& .MuiPickersDay-root": {
              fontSize: "14px",
              color: "#3E3E3E",
              fontWeight: "700",
            },
            "& .MuiDayCalendar-weekDayLabel": {
              fontSize: "14px",
              color: "#3E3E3E",
              fontWeight: "700",
            },
            "& .MuiDayCalendar-weekDayLabel:nth-of-type(1)": {
              color: "#DA384C",
            },
            "& .MuiDayCalendar-weekDayLabel:nth-of-type(7)": {
              color: "#DA384C",
            },
          }}
        />

        {/* Time Picker */}
        {type === "dateTime" && (
          <Box mt={2}>
            <Box
              onClick={() => !disabled && setShowTimePicker(!showTimePicker)}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              px={2}
              py={1}
              sx={{
                cursor: disabled ? "not-allowed" : "pointer",
                backgroundColor: showTimePicker ? "#f0fafb" : "transparent",
                borderRadius: "8px",
                transition: "all 0.2s",
              }}
            >
              <Box sx={{ display: "flex" }}>
                <ClockIcon />
                <Typography fontWeight="bold" color="#0B9DBD" marginLeft={1}>
                  時間を選択
                </Typography>
              </Box>
              <Box
                sx={{
                  transform: showTimePicker ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.3s",
                }}
              >
                <DropDownArrowIcon />
              </Box>
            </Box>

            {/* Time Picker Panel */}
            {showTimePicker && (
              <Box
                className="timePickerPanel"
                display="flex"
                justifyContent="space-around"
                alignItems={"center"}
                mt={1}
                border="1px solid #D9D9D9"
                borderRadius={"10px"}
                px={1}
                py={2}
              >
                {["hour", "minute", "second"].map((unit, index) => (
                  <React.Fragment key={unit}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      mx={1}
                    >
                      <IconButton
                        onClick={() => handleTimeChange(unit as any, "up")}
                        size="small"
                        disabled={disabled}
                        sx={{ margin: "10px", padding: "0" }}
                      >
                        <KeyboardArrowUpIcon />
                      </IconButton>
                      <Box className="timeContainer">
                        <Typography variant="h6" fontSize={"14px !important"}>
                          {selectedDate
                            ?.get(unit as any)
                            .toString()
                            .padStart(2, "0")}
                        </Typography>
                      </Box>
                      <IconButton
                        onClick={() => handleTimeChange(unit as any, "down")}
                        size="small"
                        disabled={disabled}
                        sx={{ margin: "10px", padding: "0" }}
                      >
                        <KeyboardArrowDownIcon />
                      </IconButton>
                    </Box>
                    {index !== 2 && <Box fontSize={20}>:</Box>}
                  </React.Fragment>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Buttons */}
        <Stack direction="row" spacing={2} justifyContent="center" mt={2}>
          <CustomButton
            label="戻る"
            buttonCategory="cancel"
            onClick={onCancel}
            disabled={disabled}
          />
          <CustomButton
            label="確定"
            onClick={() => onDateConfirm(selectedDate)}
            disabled={disabled}
          />
        </Stack>
      </Box>
    </LocalizationProvider>
  );
};

export const CustomDateRangePicker: React.FC<CustomDateRangePickerProps> = ({
  onDateConfirm,
  onCancel,
  initialStartDate = null,
  initialEndDate = null,
  minDate,
  maxDate,
  disabled = false,
  locale = "ja",
}) => {
  const [monthToShow, setMonthToShow] = useState<Dayjs>(dayjs());
  const [startDate, setStartDate] = useState<Dayjs | null>(initialStartDate);
  const [endDate, setEndDate] = useState<Dayjs | null>(initialEndDate);

  dayjs.locale(locale);

  const japaneseWeekdays = ["日", "月", "火", "水", "木", "金", "土"];

  const handlePrev = () => {
    setMonthToShow((prev) => prev.subtract(1, "month"));
  };

  const handleNext = () => {
    setMonthToShow((prev) => prev.add(1, "month"));
  };

  const handleSelect = (date: Dayjs | null) => {
    if (!date || disabled) return;

    // If no startDate or both selected, restart range
    if (!startDate || (startDate && endDate)) {
      setStartDate(date);
      setEndDate(null);
    } else if (date.isBefore(startDate)) {
      setEndDate(startDate);
      setStartDate(date);
    } else {
      setEndDate(date);
    }
  };

  const isInRange = (day: Dayjs) => {
    if (startDate && endDate) {
      return day.isAfter(startDate, "day") && day.isBefore(endDate, "day");
    }
    return false;
  };

  const isSelected = (day: Dayjs) => {
    return (
      (startDate && day.isSame(startDate, "day")) ||
      (endDate && day.isSame(endDate, "day"))
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
      <Box
        width={322}
        border={1}
        borderRadius={2}
        p={2}
        className="customDatePicker"
        sx={{ opacity: disabled ? 0.6 : 1 }}
      >
        {/* Selected Range Display */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box className="dateRangeContainer">
            <Typography className="dateRangeText">
              {startDate ? startDate.format("YYYY/MM/DD") : "開始日"}
            </Typography>
            <span className="dateRangIconWrapper">
              <DateRangeCalendarIcon />
            </span>
          </Box>
          <Box className="dateRangeContainer">
            <Typography className="dateRangeText">
              {endDate ? endDate.format("YYYY/MM/DD") : "終了日"}
            </Typography>
            <span className="dateRangIconWrapper">
              <DateRangeCalendarIcon />
            </span>
          </Box>
        </Box>

        {/* Header */}
        <Box display="flex" justifyContent="center" alignItems="center" mb={1}>
          <IconButton onClick={handlePrev} disabled={disabled}>
            <span className="iconContainer">
              <CalendarLeftArrowIcon />
            </span>
          </IconButton>
          <Typography variant="subtitle1" className="dateTitle">
            {monthToShow.format("YYYY年M月")}
          </Typography>
          <IconButton onClick={handleNext} disabled={disabled}>
            <span className="iconContainer">
              <CalendarRightArrowIcon />
            </span>
          </IconButton>
        </Box>

        {/* Calendar */}
        <StaticDatePicker
          displayStaticWrapperAs="desktop"
          value={monthToShow}
          onChange={handleSelect}
          dayOfWeekFormatter={(_day: string, date: Dayjs) =>
            japaneseWeekdays[date.day()]
          }
          showDaysOutsideCurrentMonth={false}
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
          sx={{
            "& .MuiPickersCalendarHeader-root": {
              display: "none",
            },
            "& .MuiPickersDay-root": {
              fontSize: "14px",
              color: "#3E3E3E",
              fontWeight: "700",
            },
            "& .MuiDayCalendar-weekDayLabel": {
              fontSize: "14px",
              color: "#3E3E3E",
              fontWeight: "700",
            },
            "& .MuiDayCalendar-weekDayLabel:nth-of-type(1)": {
              color: "#DA384C",
            },
            "& .MuiDayCalendar-weekDayLabel:nth-of-type(7)": {
              color: "#DA384C",
            },
          }}
          slots={{
            day: (props: any) => {
              const day = props.day;
              const isBetween = isInRange(day);
              const isStartOrEnd = isSelected(day);

              return (
                <Box
                  onClick={() => handleSelect(day)}
                  sx={{
                    width: 36,
                    height: 36,
                    margin: !isBetween ? "3px 4px" : "inherit",
                    padding: isBetween ? "3px 4px" : "inherit",
                    borderRadius: !isBetween ? "50%" : "inherit",
                    border: isStartOrEnd
                      ? "1.5px solid #0B9DBD"
                      : "transparent",
                    bgcolor:
                      isStartOrEnd || isBetween ? "#DFF3F7" : "transparent",
                    color: "#3e3e3e",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: disabled ? "not-allowed" : "pointer",
                    fontWeight: 700,
                    fontSize: 14,
                    "&:hover": {
                      backgroundColor: disabled ? "transparent" : "#f0fafb",
                    },
                  }}
                >
                  {day.date()}
                </Box>
              );
            },
          }}
        />

        {/* Buttons */}
        <Stack direction="row" spacing={2} justifyContent="center" mt={2}>
          <CustomButton
            label="戻る"
            buttonCategory="cancel"
            onClick={onCancel}
            disabled={disabled}
          />
          <CustomButton
            label="確定"
            onClick={() => onDateConfirm({ start: startDate, end: endDate })}
            disabled={disabled || !startDate}
          />
        </Stack>
      </Box>
    </LocalizationProvider>
  );
};
// ---------------------------------------------
// New Date & Time Picker (input style) component
// ---------------------------------------------

export interface CustomDateTimePickerProps {
  onChange: (value: string) => void;
  showTime?: boolean;
  maxDate?: Dayjs;
  minDate?: Dayjs;
  width?: any;
  value?: any;
  defaultValue?: Dayjs | null;
  locale?: string;
  error?: boolean;
  helperText?: string;
  views?: DateView[];
  openTo?: DateView;
  format?: string;
  outputFormat?: string;
}

const customTheme = createTheme({
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#D9D9D9 !important",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#D9D9D9 !important",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#D9D9D9 !important",
            borderWidth: "1px !important",
          },
        },
      },
    },
  },
});

const ReportCalendarIcon = () => (
  <svg
    width="18"
    height="20"
    viewBox="0 0 18 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12.6667 1.25V4.75M5.33333 1.25V4.75M0.75 8.25H17.25M2.58333 3H15.4167C16.4292 3 17.25 3.7835 17.25 4.75V17C17.25 17.9665 16.4292 18.75 15.4167 18.75H2.58333C1.57081 18.75 0.75 17.9665 0.75 17V4.75C0.75 3.7835 1.57081 3 2.58333 3Z"
      stroke="#3E3E3E"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const CustomDateTimePicker: React.FC<CustomDateTimePickerProps> = ({
  onChange,
  showTime = true,
  maxDate,
  minDate,
  width,
  value,
  defaultValue = dayjs(),
  locale = "ja",
  error = false,
  helperText,
  views,
  openTo,
  format,
  outputFormat,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const initialDate = value ? dayjs(value) : defaultValue || null;
  const [date, setDate] = useState<Dayjs | null>(initialDate);
  const [hour, setHour] = useState<string>("00");
  const [minute, setMinute] = useState<string>("00");
  const [isOpen, setIsOpen] = useState(false);
  const [, setError] = useState<string | null>(null);
  const scrollPositionRef = useRef<number>(0);
  const prevValueRef = useRef<string | undefined>(undefined);
  const lastEmittedValueRef = useRef<string | undefined>(undefined);
  const isInitialMountRef = useRef<boolean>(true);
  const {
    menuMaxHeight,
    menuAnchorOrigin,
    menuTransformOrigin,
    updateMenuPlacement,
  } = useResponsiveMenuPlacement(360);
  const timeMenuProps = {
    anchorOrigin: menuAnchorOrigin,
    transformOrigin: menuTransformOrigin,
    PaperProps: {
      sx: {
        maxHeight: menuMaxHeight,
        "& .MuiMenuItem-root": { fontSize: "12px !important" },
      },
    },
  };

  const defaultMinDate = dayjs().subtract(100, "year");
  const defaultMaxDate = dayjs().add(20, "year");
  const effectiveMinDate = minDate || defaultMinDate;
  const effectiveMaxDate = maxDate || defaultMaxDate;

  const hours = Array.from({ length: 24 }, (_, i) =>
    String(i).padStart(2, "0"),
  );
  const minutes = Array.from({ length: 60 }, (_, i) =>
    String(i).padStart(2, "0"),
  );
  const CalendarIcon = () => <ReportCalendarIcon />;

  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      if (value) {
        lastEmittedValueRef.current = value;
        return;
      }
    }

    if (date) {
      let newValue: string;
      if (showTime) {
        const combined = dayjs(
          `${date.format("YYYY-MM-DD")}T${hour}:${minute}:00`,
        );
        newValue = combined.utc().toISOString();
      } else {
        newValue = date.format(outputFormat ?? "YYYY-MM-DD");
      }

      if (lastEmittedValueRef.current !== newValue) {
        lastEmittedValueRef.current = newValue;
        onChange(newValue);
      }
    } else if (
      lastEmittedValueRef.current !== "" &&
      lastEmittedValueRef.current !== undefined
    ) {
      // 日付がクリアされた場合、空文字をフォームに通知
      lastEmittedValueRef.current = "";
      onChange("");
    }
  }, [date, hour, minute, onChange, showTime]);

  useEffect(() => {
    if (prevValueRef.current === value) {
      return;
    }

    if (value) {
      const parsed = dayjs(value);
      if (!parsed.isValid()) {
        prevValueRef.current = value;
        return;
      }

      const currentDateOnly = date ? date.format("YYYY-MM-DD") : null;
      const parsedDateOnly = parsed.format("YYYY-MM-DD");

      if (currentDateOnly !== parsedDateOnly) {
        requestAnimationFrame(() => {
          setDate(parsed);
        });
      }
      if (showTime) {
        const parsedHour = parsed.format("HH");
        const parsedMinute = parsed.format("mm");
        if (hour !== parsedHour) {
          requestAnimationFrame(() => {
            setHour(parsedHour);
          });
        }
        if (minute !== parsedMinute) {
          requestAnimationFrame(() => {
            setMinute(parsedMinute);
          });
        }
      }
      prevValueRef.current = value;
    } else if (value !== undefined) {
      requestAnimationFrame(() => {
        setDate(null);
      });
      if (showTime) {
        requestAnimationFrame(() => {
          setHour("00");
          setMinute("00");
        });
      }
      prevValueRef.current = value;
    }
  }, [value, showTime, date, hour, minute]);

  const handleOpen = () => {
    scrollPositionRef.current = window.scrollY;
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      window.scrollTo(0, scrollPositionRef.current);
    }, 100);
  };

  const inputStyles = {
    "& .MuiInputBase-input": {
      padding: "6px 14px !important",
      fontSize: "12px !important",
      height: { lg: "inherit !important", xs: "32px !important" },
    },
    "& .MuiInputBase-root": {
      borderRadius: "8px !important",
      height: { lg: "inherit !important", xs: "32px !important" },
    },
    "& .MuiOutlinedInput-root": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#D9D9D9 !important",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#D9D9D9 !important",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#D9D9D9 !important",
        borderWidth: "1px !important",
      },
      "&.Mui-error .MuiOutlinedInput-notchedOutline": {
        borderColor: "#d32f2f !important",
      },
      "&.Mui-error:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#D9D9D9 !important",
      },
      "&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#d32f2f !important",
        borderWidth: "1px !important",
      },
    },
    "& .MuiInputAdornment-root": {
      marginLeft: "8px",
      "& svg": {
        color: "#0e9dbc !important",
        width: "inherit",
        height: "inherit",
      },
    },
  } as const;

  const dateFormat = format ?? (isMobile ? "MM/DD" : "YYYY/MM/DD");

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
      <ThemeProvider theme={customTheme}>
        <Box width="inherit">
          <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
            <DatePicker
              value={date}
              minDate={effectiveMinDate as any}
              maxDate={effectiveMaxDate as any}
              views={views}
              openTo={openTo}
              onChange={(newValue) => {
                if (newValue && dayjs(newValue).isValid()) {
                  setDate(newValue as Dayjs);
                  setError(null);
                } else if (newValue === null) {
                  setDate(null);
                  setError(null);
                }
              }}
              onError={(newError) => {
                setError(newError ? "" : null);
              }}
              open={isOpen}
              onOpen={handleOpen}
              onClose={handleClose}
              format={dateFormat}
              dayOfWeekFormatter={(d) => {
                // 文字列が渡された場合（既にフォーマット済みの曜日名）はそのまま返す
                if (typeof d === "string") {
                  return d;
                }
                // 日付オブジェクトの場合のみdayjsで処理
                const dayjsDate = dayjs(d).locale(locale);
                if (!dayjsDate.isValid()) {
                  return "";
                }
                const result = dayjsDate.format("dd") as any;
                return result;
              }}
              localeText={{
                calendarWeekNumberHeaderText: "#",
                calendarWeekNumberText: (weekNumber) => `${weekNumber}.`,
                cancelButtonLabel: "キャンセル",
                clearButtonLabel: "クリア",
                okButtonLabel: "OK",
                todayButtonLabel: "今日",
              }}
              slotProps={{
                textField: {
                  variant: "outlined",
                  error: error,
                  helperText: helperText,
                  sx: {
                    width: {
                      sm: width || (showTime ? "40%" : "100%"),
                      xs: "100%",
                    },
                    ...inputStyles,
                  },
                },
                popper: {
                  placement: "bottom-start",
                  modifiers: [
                    {
                      name: "preventOverflow",
                      enabled: true,
                      options: {
                        altBoundary: true,
                        tether: false,
                        padding: 8,
                      },
                    },
                    {
                      name: "flip",
                      enabled: true,
                      options: {
                        fallbackPlacements: [
                          "top-start",
                          "top-end",
                          "bottom-end",
                        ],
                        altBoundary: true,
                        padding: 8,
                      },
                    },
                    {
                      name: "offset",
                      enabled: true,
                      options: { offset: [0, 8] },
                    },
                  ],
                  sx: {
                    "& .MuiPaper-root": {
                      boxShadow: "0px 4px 20px rgba(0,0,0,0.1)",
                      maxHeight: "400px !important",
                      overflow: "hidden !important",
                    },
                    "& .MuiPickersDay-root": {
                      "&.Mui-selected": {
                        backgroundColor: "#0e9dbc !important",
                        color: "white !important",
                        fontWeight: "bold !important",
                        "&:hover": { backgroundColor: "#0e9dbc !important" },
                        "&:focus": { backgroundColor: "#0e9dbc !important" },
                      },
                      "&:hover": {
                        backgroundColor: "rgba(14,157,188,0.1) !important",
                      },
                    },
                    "& .MuiPickersCalendarHeader-root": {
                      "& .MuiIconButton-root": { color: "#0e9dbc" },
                    },
                    "& .MuiYearPicker-root": {
                      maxHeight: "350px !important",
                      overflowY: "auto !important",
                      padding: "8px !important",
                    },
                    "& .MuiPickersYearPicker-root, & .PrivatePickersYearPicker-root":
                      {
                        maxHeight: "350px !important",
                        overflowY: "auto !important",
                        padding: "8px !important",
                      },
                    "& .MuiPickersView-root": {
                      maxHeight: "350px !important",
                      overflowY: "auto !important",
                    },
                    "& .MuiPickersYear-yearButton": {
                      "&.Mui-selected": {
                        backgroundColor: "#0b9cbc !important",
                        color: "white !important",
                        fontWeight: "bold !important",
                        "&:hover": { backgroundColor: "#0b9cbc !important" },
                        "&:focus": { backgroundColor: "#0b9cbc !important" },
                      },
                      "&:hover": {
                        backgroundColor: "rgba(11,156,188,0.1) !important",
                      },
                    },
                    "& .MuiPickersMonth-monthButton": {
                      "&.Mui-selected": {
                        backgroundColor: "#0b9cbc !important",
                        color: "white !important",
                        fontWeight: "bold !important",
                        "&:hover": { backgroundColor: "#0b9cbc !important" },
                        "&:focus": { backgroundColor: "#0b9cbc !important" },
                      },
                      "&:hover": {
                        backgroundColor: "rgba(11,156,188,0.1) !important",
                      },
                    },
                    "& .MuiPickersYear-root": {
                      "&.Mui-selected": {
                        backgroundColor: "#0e9dbc !important",
                        color: "white !important",
                        fontWeight: "bold !important",
                        "&:hover": { backgroundColor: "#0e9dbc !important" },
                        "&:focus": { backgroundColor: "#0e9dbc !important" },
                      },
                      "&:hover": {
                        backgroundColor: "rgba(14,157,188,0.1) !important",
                      },
                    },
                    "& .MuiPickersActionBar-root": {
                      display: "none !important",
                    },
                    "& .MuiDialogActions-root": { display: "none !important" },
                  },
                },
                layout: {
                  sx: {
                    "& .MuiPickersActionBar-root": {
                      display: "none !important",
                    },
                  },
                },
              }}
              slots={{
                openPickerIcon: CalendarIcon,
              }}
              desktopModeMediaQuery="@media (min-width: 0px)"
            />

            {showTime && (
              <>
                <TextField
                  select
                  value={hour}
                  onChange={(e) => setHour(e.target.value)}
                  sx={{
                    width: "25%",
                    "& .MuiSelect-select": {
                      padding: "6px 14px !important",
                      fontSize: "12px !important",
                    },
                    ...inputStyles,
                  }}
                  SelectProps={{
                    onOpen: (event) =>
                      updateMenuPlacement(event.currentTarget as HTMLElement),
                    IconComponent: () => (
                      <span style={{ fontSize: "12px", paddingRight: "14px" }}>
                        時
                      </span>
                    ),
                    MenuProps: timeMenuProps,
                  }}
                >
                  {hours.map((h) => (
                    <MenuItem key={h} value={h}>
                      {h}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  value={minute}
                  onChange={(e) => setMinute(e.target.value)}
                  sx={{
                    width: "25%",
                    "& .MuiSelect-select": {
                      padding: "6px 14px !important",
                      fontSize: "12px !important",
                    },
                    ...inputStyles,
                  }}
                  SelectProps={{
                    onOpen: (event) =>
                      updateMenuPlacement(event.currentTarget as HTMLElement),
                    IconComponent: () => (
                      <span style={{ fontSize: "12px", paddingRight: "14px" }}>
                        分
                      </span>
                    ),
                    MenuProps: timeMenuProps,
                  }}
                >
                  {minutes.map((m) => (
                    <MenuItem key={m} value={m}>
                      {m}
                    </MenuItem>
                  ))}
                </TextField>
              </>
            )}
          </Stack>
        </Box>
      </ThemeProvider>
    </LocalizationProvider>
  );
};
